<template>
    <div class="permission-management-container">
        <!-- Header Section -->
        <el-card class="header-card" shadow="never">
            <el-row :gutter="20" align="middle">
                <el-col :span="24">
                    <h2 style="margin: 0; color: #303133;">Manajemen Hak Akses</h2>
                    <p style="margin: 5px 0 0 0; color: #909399; font-size: 14px;">Kelola peran dan hak akses pengguna dalam sistem</p>
                </el-col>
            </el-row>
        </el-card>

        <!-- Main Content -->
        <el-row :gutter="20">
            <!-- Role List Section -->
            <el-col :md="8">
                <el-card shadow="never">
                    <template #header>
                        <div class="card-header">
                            <span style="font-weight: bold;">Daftar Peran</span>
                            <el-button 
                                type="primary" 
                                size="small"
                                @click="showAddRole()"
                                style="background-color: #409EFF; border-color: #409EFF; color: white;"
                            >
                                <el-icon style="margin-right: 5px;"><Plus /></el-icon>
                                Tambah
                            </el-button>
                        </div>
                    </template>
                    
                    <el-table 
                        :data="roleList" 
                        v-loading="loadingRole" 
                        :stripe="true"
                        @row-click="selectRole"
                        :highlight-current-row="true"
                        style="width: 100%"  
                        height="600"
                        empty-text="Tidak ada data peran"
                    >
                        <el-table-column prop="module_name" label="Modul" min-width="120" />
                        <el-table-column prop="role_name" label="Peran" min-width="100" />
                        <el-table-column width="100" label="Aksi" align="center">
                            <template #default="scope">
                                <div style="display: flex; gap: 5px; justify-content: center;">
                                    <el-tooltip content="Ubah Peran" placement="top">
                                        <el-button 
                                            size="small" 
                                            type="primary"
                                            style="background-color: #409EFF; border-color: #409EFF; color: white;"
                                            @click.stop="showEditRole(scope)"
                                        >
                                            <el-icon><Edit /></el-icon>
                                        </el-button>
                                    </el-tooltip>
                                    <el-tooltip content="Hapus Peran" placement="top">
                                        <el-popconfirm 
                                            title="Apakah Anda yakin ingin menghapus peran ini?" 
                                            confirm-button-text="Ya"
                                            cancel-button-text="Tidak"
                                            @confirm="deleteRole(scope)"
                                        >
                                            <template #reference>
                                                <el-button 
                                                    size="small" 
                                                    type="danger"
                                                    style="background-color: #F56C6C; border-color: #F56C6C; color: white;"
                                                    @click.stop=""
                                                >
                                                    <el-icon><Delete /></el-icon>
                                                </el-button>
                                            </template>
                                        </el-popconfirm>
                                    </el-tooltip>
                                </div>
                            </template>
                        </el-table-column>
                    </el-table>
                </el-card>
            </el-col>

            <!-- Permission Details Section -->
            <el-col :md="16">
                <el-card shadow="never" v-if="selectedRole.id" v-loading="loadingACL">
                    <template #header>
                        <div style="display: flex; align-items: center;">
                            <el-icon style="margin-right: 8px; color: #409EFF;"><UserFilled /></el-icon>
                            <span style="font-weight: bold; font-size: 16px;">
                                {{ selectedRole.module_name }} - {{ selectedRole.role_name }}
                            </span>
                        </div>
                        <p style="margin: 8px 0 0 28px; color: #909399; font-size: 14px;">
                            {{ selectedRole.description || 'Tidak ada deskripsi' }}
                        </p>
                    </template>

                    <!-- Feature Permissions -->
                    <div v-if="ACList && ACList.length > 0" style="margin-bottom: 30px;">
                        <div class="permission-section-header">
                            <h4>Hak Akses Fitur</h4>
                            <el-button 
                                type="primary" 
                                size="small"
                                @click="saveRolePermission()"
                                :loading="savingPermission"
                                style="background-color: #67C23A; border-color: #67C23A; color: white;"
                            >
                                <el-icon style="margin-right: 5px;"><Check /></el-icon>
                                Simpan
                            </el-button>
                        </div>
                        
                        <div class="permission-list">
                            <div v-for="(acl, index) in ACList" :key="index" class="permission-item">
                                <el-checkbox v-model="acl.permission" style="width: 100%;">
                                    <div style="display: flex; align-items: center; width: 100%;">
                                        <span style="font-weight: 500; margin-right: 10px;">{{ acl.access }}</span>
                                        <span style="color: #909399; font-size: 13px;">{{ acl.access_description }}</span>
                                    </div>
                                </el-checkbox>
                            </div>
                        </div>
                    </div>

                    <!-- Activity Permissions (for module_id 2) -->
                    <div v-if="selectedRole.module_id == '2' && flowList.length > 0">
                        <div class="permission-section-header">
                            <h4>Hak Akses Aktivitas</h4>
                            <div style="display: flex; gap: 8px;">
                                <el-button 
                                    size="small"
                                    @click="toggleAllFlow()"
                                    style="background-color: #409EFF; border-color: #409EFF; color: white;"
                                >
                                    <el-icon style="margin-right: 5px;">
                                        <ArrowDown v-if="!isAllExpanded" />
                                        <ArrowUp v-else />
                                    </el-icon>
                                    {{ isAllExpanded ? 'Tutup Semua' : 'Buka Semua' }}
                                </el-button>
                                <el-button 
                                    type="primary" 
                                    size="small"
                                    @click="saveFlowPermission()"
                                    :loading="savingFlow"
                                    style="background-color: #67C23A; border-color: #67C23A; color: white;"
                                >
                                    <el-icon style="margin-right: 5px;"><Check /></el-icon>
                                    Simpan
                                </el-button>
                            </div>
                        </div>
                        
                        <div class="flow-permission-list">
                            <el-collapse v-model="activeCollapseNames" ref="flowCollapse">
                                <el-collapse-item 
                                    v-for="(flow, index) in flowList" 
                                    :key="index"
                                    :name="index"
                                >
                                    <template #title>
                                        <!-- <el-checkbox 
                                            v-model="sellectedSiputraRules[flow.id]['access']"
                                            @click.stop
                                            style="margin-right: 10px;"
                                        />-->
                                        <span style="font-weight: 500;">{{ flow.group_name }} - {{ flow.flow_name }}</span>
                                        <el-tag v-if="flow.description" size="small" style="margin-left: 10px;" type="info">
                                            {{ flow.description }}
                                        </el-tag>
                                    </template>
                                    
                                    <div class="flow-permissions">
                                        <el-checkbox 
                                            v-for="opt in siputraRules" 
                                            :key="opt.name"
                                            v-model="sellectedSiputraRules[flow.id][opt.name]" 
                                            :label="opt.label"
                                            style="display: block; margin: 8px 0;"
                                        />
                                    </div>
                                </el-collapse-item>
                            </el-collapse>
                        </div>
                    </div>
                </el-card>

                <!-- Empty State -->
                <el-card shadow="never" v-else>
                    <el-empty description="Pilih peran untuk melihat dan mengelola hak akses" />
                </el-card>
            </el-col>
        </el-row>

        <!-- Role Dialog -->
        <el-dialog 
            v-model="dialogRoleVisible" 
            :title="selectedRole.id ? 'Ubah Peran' : 'Tambah Peran Baru'" 
            width="600px"
            :close-on-click-modal="false"
        >
            <el-form 
                :model="selectedRole" 
                label-width="140px" 
                :rules="RoleRules" 
                ref="ruleRoleForm"
                label-position="right"
            >
                <el-form-item label="Nama Peran" prop="role_name">
                    <el-input 
                        v-model="selectedRole.role_name" 
                        placeholder="Masukkan nama peran (contoh: Admin, Operator)"
                    />
                </el-form-item>
                <el-form-item label="Modul" prop="module_id">
                    <el-select 
                        v-model="selectedRole.module_id" 
                        placeholder="Pilih modul"
                        style="width: 100%"
                        :loading="loadingModules"
                    >
                        <el-option 
                            v-for="item in moduleList" 
                            :key="item.id" 
                            :label="item.module_name" 
                            :value="item.id" 
                        />
                    </el-select>
                </el-form-item>
                <el-form-item label="Deskripsi" prop="description">
                    <el-input 
                        v-model="selectedRole.description" 
                        type="textarea"
                        :rows="3"
                        placeholder="Masukkan deskripsi peran (opsional)"
                    />
                </el-form-item>
            </el-form>
            <template #footer>
                <span class="dialog-footer">
                    <el-button @click="dialogRoleVisible = false">Batal</el-button>
                    <el-button 
                        type="primary" 
                        @click="selectedRole.id ? editRole() : addRole()"
                        :loading="submitting"
                    >
                        {{ selectedRole.id ? 'Simpan' : 'Tambah' }}
                    </el-button>
                </span>
            </template>
        </el-dialog>
    </div>
</template>

<script>
import axios from 'axios'
import { ElMessage } from 'element-plus'
import { 
    Plus, Edit, Delete, Check, UserFilled, ArrowDown, ArrowUp
} from '@element-plus/icons-vue'

export default {
    name: 'PermissionView',
    data() {
        return {
            Plus, Edit, Delete, Check, UserFilled, ArrowDown, ArrowUp,
            tableList: [],
            ACList: [],
            flowList: [],
            flowListPermission: [],
            loadingFlow: false,
            loadingModules: false,
            moduleList: [],
            roleList: [],
            selectedRole: { flow_permissions: [] },
            selectedFlows: [],
            dialogRoleVisible: false,
            loadingRole: false,
            loadingACL: false,
            savingPermission: false,
            savingFlow: false,
            submitting: false,
            selectedFlow: null,
            selectedAccess: {},
            sellectedSiputraRules: {},
            activeCollapseNames: [],
            siputraRules: [{
                name: 'Manage Records',
                label: 'Kelola Data',
            },{
                name: 'Manage My Records',
                label: 'Kelola Data Saya',
            }, {
                name: 'View Records',
                label: 'Lihat Data',
            }, {
                name: 'Manage Notes',
                label: 'Kelola Catatan',
            }, {
                name: 'Manage My Notes',
                label: 'Kelola Catatan Saya',
            }, {
                name: 'View Notes',
                label: 'Lihat Catatan',
            }],
            RoleRules: {
                role_name: [
                    { required: true, message: 'Harap masukkan nama peran', trigger: 'blur' },
                    { min: 3, message: 'Nama peran minimal 3 karakter', trigger: 'blur' }
                ],
                module_id: [
                    { required: true, message: 'Harap pilih modul', trigger: 'change' }
                ],
                description: [
                    { required: false, message: 'Harap masukkan deskripsi', trigger: 'blur' }
                ]
            }
        }
    },
    computed: {
        apiHeaders() {
            return {
                'Authorization': `Bearer ${this.$store.state.auth_token}`
            }
        },
        baseUrl() {
            return this.$store.state.base_url
        },
        isAllExpanded() {
            return this.activeCollapseNames.length === this.flowList.length
        }
    },
    mounted() {
        this.getRole()
        this.getModule()
        this.getFlow()
    },
    methods: {
        async getModule() {
            this.loadingModules = true
            try {
                const response = await axios.get(`${this.baseUrl}/api/modules`, {
                    headers: this.apiHeaders
                })
                this.moduleList = response.data.data
            } catch (error) {
                this.handleError(error, 'Gagal mengambil data modul')
            } finally {
                this.loadingModules = false
            }
        },
        
        async getFlow() {
            this.loadingFlow = true
            try {
                const response = await axios.get(`${this.baseUrl}/api/flows`, {
                    headers: this.apiHeaders
                })
                
                this.flowList = response.data.data
                this.flowList.forEach(element => {
                    this.sellectedSiputraRules[element.id] = []
                })
            } catch (error) {
                this.handleError(error, 'Gagal mengambil data alur')
            } finally {
                this.loadingFlow = false
            }
        },
        
        async getRole() {
            this.loadingRole = true
            try {
                const response = await axios.get(`${this.baseUrl}/api/roles`, {
                    headers: this.apiHeaders
                })
                
                this.roleList = response.data.data
            } catch (error) {
                this.handleError(error, 'Gagal mengambil data peran')
            } finally {
                this.loadingRole = false
            }
        },
        
        async getRoleById(role_id) {
            this.loadingACL = true
            this.flowListPermission = []
            try {
                const response = await axios.get(`${this.baseUrl}/api/role/${role_id}`, {
                    headers: this.apiHeaders
                })
                
                this.ACList = response.data.acl
                this.flowListPermission = response.data.flows
                
                const resetPerm = { 
                        'Manage Records':false,
                        'Manage My Records':false,
                        'View Records':false,
                        'Manage Notes':false,
                        'Manage My Notes':false,
                        'View Notes':false
                }       
                this.sellectedSiputraRules={}
                this.flowList.forEach(element => {
                    this.sellectedSiputraRules[element.id] = {...resetPerm}
                })
                this.flowListPermission.forEach(el => {
                    this.sellectedSiputraRules[el.id] = el.permissions
                })
            } catch (error) {
                this.handleError(error, 'Gagal mengambil detail peran')
            } finally {
                this.loadingACL = false
            }
        },
        
        selectRole(row) {
            this.selectedRole = row
            this.ACList = []
            this.getRoleById(row.id)
        },
        
        showAddRole() {
            this.resetForm()
            this.dialogRoleVisible = true
        },
        
        showEditRole(selected) {
            this.selectedRole = { ...selected.row }
            this.dialogRoleVisible = true
        },
        
        resetForm() {
            this.selectedRole = { 
                id: '',
                role_name: '',
                module_id: '',
                description: ''
            }
        },
        
        async addRole() {
            try {
                const valid = await this.$refs['ruleRoleForm'].validate()
                if (!valid) return
                
                this.submitting = true
                const postData = {
                    role_name: this.selectedRole.role_name,
                    module_id: this.selectedRole.module_id,
                    description: this.selectedRole.description
                }
                
                await axios.post(`${this.baseUrl}/api/role`, postData, {
                    headers: this.apiHeaders
                })
                
                ElMessage({
                    message: 'Peran berhasil ditambahkan!',
                    type: 'success'
                })
                this.dialogRoleVisible = false
                this.getRole()
            } catch (error) {
                this.handleError(error, 'Gagal menambahkan peran')
            } finally {
                this.submitting = false
            }
        },
        
        async editRole() {
            try {
                const valid = await this.$refs['ruleRoleForm'].validate()
                if (!valid) return
                
                this.submitting = true
                const postData = {
                    role_name: this.selectedRole.role_name,
                    module_id: this.selectedRole.module_id,
                    description: this.selectedRole.description
                }
                
                await axios.put(`${this.baseUrl}/api/role/${this.selectedRole.id}`, postData, {
                    headers: this.apiHeaders
                })
                
                this.dialogRoleVisible = false
                ElMessage({
                    message: 'Peran berhasil diperbarui!',
                    type: 'success'
                })
                this.getRole()
            } catch (error) {
                this.handleError(error)
            } finally {
                this.submitting = false
            }
        },
        
        async saveRolePermission() {
            this.savingPermission = true
            try {
                let postData = { ...this.selectedRole }
                postData.permissions = []
                
                this.ACList.forEach(val => {
                    if (val.permission) {
                        postData.permissions.push(val.access)
                    }
                })
                postData.permissions = postData.permissions.join(",")
                
                await axios.put(`${this.baseUrl}/api/role/${this.selectedRole.id}`, postData, {
                    headers: this.apiHeaders
                })
                
                ElMessage({
                    message: 'Hak akses berhasil disimpan!',
                    type: 'success'
                })
                this.getRole()
            } catch (error) {
                this.handleError(error, 'Gagal menyimpan hak akses')
            } finally {
                this.savingPermission = false
            }
        },
        
        async saveFlowPermission() {
            this.savingFlow = true
            try {
                let postData = { ...this.selectedRole }
                postData.flow_permissions = []
                console.log("this.sellectedSiputraRules",this.sellectedSiputraRules)
                Object.keys(this.sellectedSiputraRules).forEach(key => {
                    if(Object.keys(this.sellectedSiputraRules[key]).length>0){
                        let temp = { permissions: [] }
                            temp.flow_id = key
                            Object.keys(this.sellectedSiputraRules[key]).forEach((keyx) => {
                                if(this.sellectedSiputraRules[key][keyx] && keyx !== 'access'){
                                    temp.permissions.push(keyx)
                                }
                        })
                        if(temp.permissions.length>0){
                            postData.flow_permissions.push(temp)
                        }
                    }
                })
                
                await axios.put(`${this.baseUrl}/api/role/${this.selectedRole.id}/flow_permission`, postData, {
                    headers: this.apiHeaders
                })
                
                ElMessage({
                    message: 'Hak akses aktivitas berhasil disimpan!',
                    type: 'success'
                })
                this.getRole()
            } catch (error) {
                this.handleError(error, 'Gagal menyimpan hak akses aktivitas')
            } finally {
                this.savingFlow = false
            }
        },
        
        async deleteRole(selected) {
            this.loadingRole = true
            try {
                await axios.delete(`${this.baseUrl}/api/role/${selected.row.id}`, {
                    headers: this.apiHeaders
                })
                
                ElMessage({
                    message: 'Peran berhasil dihapus!',
                    type: 'success'
                })
                this.getRole()
                
                if (this.selectedRole.id === selected.row.id) {
                    this.selectedRole = { flow_permissions: [] }
                    this.ACList = []
                }
            } catch (error) {
                this.handleError(error)
            } finally {
                this.loadingRole = false
            }
        },
        
        toggleAllFlow() {
            if (this.isAllExpanded) {
                this.activeCollapseNames = []
            } else {
                this.activeCollapseNames = this.flowList.map((_, index) => index)
            }
        },
        
        handleError(error, defaultMessage = '') {
            const message = error.response?.data?.message || defaultMessage || 'Terjadi kesalahan'
            ElMessage({
                message,
                type: 'error'
            })
            console.error('Error:', error)
        }
    }
}
</script>

<style scoped>
.permission-management-container {
    padding: 20px;
}

.header-card {
    margin-bottom: 20px;
}

.card-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.permission-section-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
    padding-bottom: 10px;
    border-bottom: 1px solid #EBEEF5;
}

.permission-section-header h4 {
    margin: 0;
    color: #303133;
}

.permission-list {
    background-color: #f5f7fa;
    padding: 15px;
    border-radius: 4px;
}

.permission-item {
    padding: 10px;
    margin-bottom: 8px;
    background-color: white;
    border-radius: 4px;
    border: 1px solid #EBEEF5;
}

.permission-item:last-child {
    margin-bottom: 0;
}

.flow-permission-list {
    padding: 15px;
    border-radius: 4px;
}

.flow-permissions {
    padding: 15px;
    background-color: #f5f7fa;
    border-radius: 4px;
}

.el-table {
    cursor: pointer;
}

.el-table__row:hover {
    background-color: #f5f7fa !important;
}

@media (max-width: 768px) {
    .permission-management-container {
        padding: 10px;
    }
    
    .el-dialog {
        width: 90% !important;
    }
    
    .el-col-md-8,
    .el-col-md-16 {
        margin-bottom: 20px;
    }
}
</style>